import React, { useState, useRef, useEffect } from 'react';
import CropTool from './CropTool';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faRotateLeft, faRotateRight, faCrop, faSync, faCheck, faTimes, faInfoCircle } from '@fortawesome/free-solid-svg-icons';

const ImageAdjustmentModal = ({ imageUrl, rotation, setRotation, crop, onSave, onCancel }) => {
    const [loading, setLoading] = useState(true);
    const [activeTool, setActiveTool] = useState('rotate'); // 'rotate' or 'crop'
    const [currentCrop, setCurrentCrop] = useState(crop || { x: 10, y: 10, width: 80, height: 80 });
    const imageRef = useRef(null);
    const [currentRotation, setCurrentRotation] = useState(rotation || 0);

    // Handle rotation in 90-degree increments
    const handleRotateLeft = () => {
        setCurrentRotation((prev) => (prev - 90) % 360);
    };

    const handleRotateRight = () => {
        setCurrentRotation((prev) => (prev + 90) % 360);
    };

    const handleSave = () => {
        console.log("Saving adjustments:", { rotation: currentRotation, crop: currentCrop });
        onSave(currentRotation, currentCrop);
    };

    const handleCropChange = (newCrop) => {
        console.log("Crop changed:", newCrop);
        setCurrentCrop(newCrop);
    };

    // Reset image loading state when the image changes
    useEffect(() => {
        setLoading(true);
    }, [imageUrl]);

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg shadow-xl w-full max-w-xl overflow-hidden">
                <div className="p-4 border-b border-gray-200">
                    <h2 className="text-lg font-semibold" style={{ fontFamily: "Playfair Display" }}>
                        Adjust Image
                    </h2>
                </div>

                {/* Tool Selection Tabs */}
                <div className="flex border-b border-gray-200">
                    <button
                        className={`px-4 py-2 font-medium ${activeTool === 'rotate' ? 'text-blue-600 border-b-2 border-blue-600' : 'text-gray-500'}`}
                        onClick={() => setActiveTool('rotate')}
                    >
                        <FontAwesomeIcon icon={faSync} className="mr-2" />
                        Rotate
                    </button>
                    <button
                        className={`px-4 py-2 font-medium ${activeTool === 'crop' ? 'text-blue-600 border-b-2 border-blue-600' : 'text-gray-500'}`}
                        onClick={() => setActiveTool('crop')}
                    >
                        <FontAwesomeIcon icon={faCrop} className="mr-2" />
                        Crop
                    </button>
                </div>
                
                {/* Important Note about rotation and cropping order */}
                <div className="bg-blue-50 text-blue-800 p-2 text-sm flex items-start">
                    <FontAwesomeIcon icon={faInfoCircle} className="mr-2 mt-0.5 flex-shrink-0" />
                    <span>For best results, please complete rotation adjustments before cropping the image.</span>
                </div>

                <div className="p-4">
                    {activeTool === 'rotate' ? (
                        <div className="flex flex-col items-center">
                            <div className="relative w-64 h-64 mb-4 overflow-hidden rounded-lg">
                                <img
                                    src={imageUrl}
                                    alt="Adjustable"
                                    className="w-full h-full object-contain"
                                    style={{ transform: `rotate(${currentRotation}deg)` }}
                                />
                            </div>
                            <div className="flex space-x-4">
                                <button
                                    onClick={handleRotateLeft}
                                    className="bg-gray-100 hover:bg-gray-200 p-3 rounded-full"
                                >
                                    <FontAwesomeIcon icon={faRotateLeft} />
                                </button>
                                <button
                                    onClick={handleRotateRight}
                                    className="bg-gray-100 hover:bg-gray-200 p-3 rounded-full"
                                >
                                    <FontAwesomeIcon icon={faRotateRight} />
                                </button>
                            </div>
                        </div>
                    ) : (
                        <div className="flex flex-col items-center">
                            <CropTool 
                                imageUrl={imageUrl} 
                                initialCrop={currentCrop}
                                rotation={currentRotation} 
                                onCropChange={handleCropChange}
                            />
                            <div className="mt-4 text-sm text-gray-600">
                                Drag the corners to adjust the crop area.
                            </div>
                        </div>
                    )}
                </div>

                <div className="p-4 border-t border-gray-200 flex justify-end space-x-2">
                    <button
                        onClick={onCancel}
                        className="px-4 py-2 text-gray-600 bg-gray-100 rounded hover:bg-gray-200"
                    >
                        <FontAwesomeIcon icon={faTimes} className="mr-2" />
                        Cancel
                    </button>
                    <button
                        onClick={handleSave}
                        className="px-4 py-2 text-white bg-blue-500 rounded hover:bg-blue-600"
                    >
                        <FontAwesomeIcon icon={faCheck} className="mr-2" />
                        Apply
                    </button>
                </div>
            </div>
        </div>
    );
};

export default ImageAdjustmentModal;
